import { useCallback, useMemo, useRef, useState } from "react";
import { Col, Form, Row, Tooltip, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  LoadingOutlined,
  LockOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { openConfirmNotification, openErrorNotification } from "../utils/toast";
import CommonButton from "./CommonButton";
import ConfirmModal from "./ConfirmModal";
import axios from "axios";
import useGet from "../hooks/useGet";
import { getDealerNo } from "../hooks/getUserRole";
import CustomSelect from "./inputWidgets/CustomSelect";
import { booleanOptions } from "../../utils/Common";
import {
  brandKeyFromCard,
  CardBrandIcon,
  formatExpiresOn,
  CARD_EXPIRATION_ERROR_MESSAGE,
  isCardExpirationAcceptable,
  MAX_SAVED_CARDS,
  normalizeCardsResponse,
} from "../utils/cardPaymentUtils";

const { Text } = Typography;

/** Square SDK Pay button — hidden; Apply triggers tokenize. */
const SQUARE_CARD_BUTTON_SELECTOR =
  "#manage-cards-square-root #rswp-card-button";

function getApiBase() {
  const raw = process.env.REACT_APP_API_URL;
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s || s === "undefined") return "";
  return s.replace(/\/+$/, "");
}

const apiBase = getApiBase();

function apiUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return apiBase ? `${apiBase}${p}` : p;
}

/**
 * Dealer Payment Method CRUD (DealerCards fields).
 * List/Add:  /api/dealers/{dealerNo}/cards/
 * Set default / Delete:  /api/dealers/{dealerNo}/cards/{cardNo}/
 */
export const ManageCardsModal = ({
  setIsModalOpen,
  dealerNo: dealerNoProp,
  requiredMessage,
}) => {
  const [form] = Form.useForm();
  /** list | add */
  const [view, setView] = useState("list");
  const [saving, setSaving] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);
  const [defaultingKey, setDefaultingKey] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const pendingRef = useRef(false);

  const sessionDealerNo = getDealerNo();
  const dealerNo =
    dealerNoProp != null && dealerNoProp !== ""
      ? dealerNoProp
      : sessionDealerNo;
  const dealerNoStr =
    dealerNo != null && dealerNo !== "" ? String(dealerNo) : "";

  const applicationId = process.env.REACT_APP_APPLICATION_ID;
  const locationId = process.env.REACT_APP_LOCATION_ID;

  const cardsCollectionUrl = useMemo(() => {
    if (!dealerNoStr) return null;
    return apiUrl(`/api/dealers/${dealerNoStr}/cards/`);
  }, [dealerNoStr]);

  const { data, loading, error, refetch } = useGet(cardsCollectionUrl);

  const cards = useMemo(() => normalizeCardsResponse(data), [data]);

  const authHeaders = useCallback(() => {
    const token = JSON.parse(sessionStorage.getItem("userData"));
    return { Authorization: `Bearer ${token?.access_token}` };
  }, []);

  const resetForm = useCallback(() => {
    form.resetFields();
    setSaving(false);
    pendingRef.current = false;
  }, [form]);

  const handleBackToList = () => {
    setView("list");
    resetForm();
  };

  const openAdd = () => {
    if (cards.length >= MAX_SAVED_CARDS) {
      openErrorNotification(
        `You can save up to ${MAX_SAVED_CARDS} cards. Remove one to add another.`,
      );
      return;
    }
    form.resetFields();
    form.setFieldsValue({
      IsDefault: cards.length === 0 ? 1 : 0,
    });
    setView("add");
  };

  const handleTokenReceived = useCallback(
    async (tokenResult) => {
      setSaving(false);
      pendingRef.current = false;

      if (tokenResult?.status !== "OK" || !tokenResult?.token) {
        const err0 = tokenResult?.errors?.[0];
        const msg =
          (typeof err0 === "object" && err0?.message) ||
          (err0 instanceof Error ? err0.message : null) ||
          (typeof err0 === "string" ? err0 : null) ||
          "Could not tokenize card. Check card details.";
        openErrorNotification(String(msg));
        return;
      }

      const cardDetails = tokenResult?.details?.card;
      if (
        cardDetails?.expMonth != null &&
        cardDetails?.expYear != null &&
        !isCardExpirationAcceptable(cardDetails.expMonth, cardDetails.expYear)
      ) {
        openErrorNotification(CARD_EXPIRATION_ERROR_MESSAGE);
        return;
      }

      if (!dealerNoStr || !cardsCollectionUrl) {
        openErrorNotification("Dealer account not found.");
        return;
      }

      const payload = {
        PaymentSourceID: String(tokenResult.token),
        IsDefault: Number(form.getFieldValue("IsDefault") ?? 0) ? 1 : 0,
      };

      try {
        await axios.post(cardsCollectionUrl, payload, {
          headers: authHeaders(),
        });
        openConfirmNotification("Success");
        resetForm();
        setView("list");
        refetch?.();
      } catch (err) {
        openErrorNotification(
          err?.response?.data?.message || "Could not save card.",
        );
      }
    },
    [authHeaders, cardsCollectionUrl, dealerNoStr, form, refetch, resetForm],
  );

  const handleSaveCard = async () => {
    if (!applicationId || !locationId) {
      openErrorNotification(
        "Square is not configured (application or location ID missing).",
      );
      return;
    }
    if (!dealerNoStr) {
      openErrorNotification("Dealer account not found.");
      return;
    }
    setSaving(true);
    pendingRef.current = true;
    requestAnimationFrame(() => {
      const btn = document.querySelector(SQUARE_CARD_BUTTON_SELECTOR);
      if (btn) {
        btn.click();
      } else {
        document.querySelector("#manage-cards-square-root button")?.click();
      }
      if (pendingRef.current) {
        setTimeout(() => {
          if (pendingRef.current) {
            pendingRef.current = false;
            setSaving(false);
            openErrorNotification(
              "Card form is not ready. Wait a moment and try again.",
            );
          }
        }, 15000);
      }
    });
  };

  const handleSetDefault = async (card) => {
    if (!dealerNoStr || card?.DealerCardNo == null) {
      openErrorNotification("Could not update default card.");
      return;
    }
    if (card.IsDefault) return;

    const cardNo = card.DealerCardNo;
    const url = apiUrl(`/api/dealers/${dealerNoStr}/cards/${cardNo}/`);
    setDefaultingKey(String(cardNo));
    try {
      await axios.put(
        url,
        { IsDefault: 1 },
        { headers: authHeaders() },
      );
      openConfirmNotification("Success");
      refetch?.();
    } catch (err) {
      openErrorNotification(
        err?.response?.data?.message || "Could not update default card.",
      );
    } finally {
      setDefaultingKey(null);
    }
  };

  const openDeleteConfirm = (card) => {
    if (!dealerNoStr || card?.DealerCardNo == null) {
      openErrorNotification("Could not remove card.");
      return;
    }
    setCardToDelete(card);
    setShowDeleteModal(true);
  };

  const handleDeleteCard = async () => {
    const card = cardToDelete;
    if (!dealerNoStr || card?.DealerCardNo == null) {
      openErrorNotification("Could not remove card.");
      setCardToDelete(null);
      return;
    }
    const cardNo = card.DealerCardNo;
    const url = apiUrl(`/api/dealers/${dealerNoStr}/cards/${cardNo}/`);
    setDeletingKey(String(cardNo));
    try {
      await axios.delete(url, { headers: authHeaders() });
      openConfirmNotification("Success");
      refetch?.();
    } catch (err) {
      openErrorNotification(
        err?.response?.data?.message || "Could not remove card.",
      );
    } finally {
      setDeletingKey(null);
      setCardToDelete(null);
    }
  };

  const shellHeader = (title) => (
    <div className="bg-gray-200 py-[8px] px-[3px] rounded-sm flex items-center mb-2 p-2">
      <CreditCardOutlined className="ml-2 text-[16px]" />
      <h2 className="text-[14px] font-[Poppins] text-black ml-2">{title}</h2>
    </div>
  );

  const busy = deletingKey != null || defaultingKey != null;

  if (!dealerNoStr) {
    return (
      <div className="bg-white rounded-[4px] overflow-hidden">
        {shellHeader("Payment Method")}
        <div className="mt-4 px-2">
          <Text type="danger">
            No dealer account is associated with this login. Payment method is
            only available for dealer accounts.
          </Text>
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton
            buttonsData={[
              {
                label: "Cancel",
                icon: <CloseOutlined />,
                onClick: () => setIsModalOpen(false),
              },
            ]}
          />
        </div>
      </div>
    );
  }

  if (!applicationId || !locationId) {
    return (
      <div className="bg-white rounded-[4px] overflow-hidden">
        {shellHeader("Payment Method")}
        <div className="mt-4 px-2">
          <Text type="danger">
            Square application ID or location ID is missing from environment
            variables.
          </Text>
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton
            buttonsData={[
              {
                label: "Cancel",
                icon: <CloseOutlined />,
                onClick: () => setIsModalOpen(false),
              },
            ]}
          />
        </div>
      </div>
    );
  }

  if (view === "list") {
    return (
      <div className="bg-white rounded-[4px] overflow-hidden">
        {shellHeader("Payment Method")}
        <div className="mt-3 px-2">
          {error ? (
            <Text type="danger" className="pl-0.5 text-sm">
              {error?.response?.data?.message ||
                "Could not load payment methods."}
            </Text>
          ) : loading ? (
            <p className="text-sm text-black font-[Poppins] pl-0.5 m-0">
              Loading…
            </p>
          ) : cards.length === 0 ? (
            <p
              className={`text-sm font-[Poppins] pl-0.5 m-0 ${
                requiredMessage ? "text-[#cf1322]" : "text-black"
              }`}
            >
              {requiredMessage || "No payment method added yet."}
            </p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {cards.map((card, idx) => {
                const key = card.DealerCardNo ?? idx;
                const expiresOn = formatExpiresOn(card);
                const deleting =
                  deletingKey != null &&
                  String(deletingKey) === String(card.DealerCardNo);
                const defaulting =
                  defaultingKey != null &&
                  String(defaultingKey) === String(card.DealerCardNo);
                const titleParts = [
                  card.CardBrand || null,
                  card.CardLast4 ? `•••• ${card.CardLast4}` : null,
                ].filter(Boolean);
                const title =
                  titleParts.length > 0 ? titleParts.join(" ") : "Saved card";

                return (
                  <div
                    key={String(key)}
                    className={`rounded-md border px-3 py-2.5 flex items-center gap-3 transition-colors ${
                      card.IsDefault
                        ? "border-[var(--text-color,#1677ff)]/30 bg-[var(--text-color-op-10,#e6f4ff)]"
                        : "border-[#e5e7eb] bg-white hover:border-[#cbd5e1]"
                    }`}
                  >
                    <CardBrandIcon brandKey={brandKeyFromCard(card)} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13px] font-medium font-[Poppins] m-0 text-black leading-snug">
                          {title}
                        </p>
                        {card.IsDefault ? (
                          <span className="inline-flex items-center gap-1 rounded-sm px-2 py-[3px] text-[11px] font-medium font-[Poppins] leading-none text-white bg-[var(--text-color,#1677ff)]">
                            <StarFilled className="text-[10px]" />
                            Default card
                          </span>
                        ) : null}
                      </div>
                      <p className="text-[11px] text-[#64748b] m-0 mt-1 font-[Poppins] leading-snug">
                        {expiresOn || "Saved payment method"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!card.IsDefault ? (
                        <Tooltip title="Set as default">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleSetDefault(card)}
                            className="cursor-pointer hover:bg-white/80 px-[6px] py-[6px] rounded-[4px] text-[#50565e] disabled:opacity-40 disabled:cursor-not-allowed bg-transparent border-0"
                            aria-label="Set as default"
                          >
                            {defaulting ? (
                              <LoadingOutlined />
                            ) : (
                              <StarOutlined />
                            )}
                          </button>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Delete">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => openDeleteConfirm(card)}
                          className="cursor-pointer hover:bg-white/80 px-[6px] py-[6px] rounded-[4px] text-[#50565e] disabled:opacity-40 disabled:cursor-not-allowed bg-transparent border-0"
                          aria-label="Delete"
                        >
                          {deleting ? <LoadingOutlined /> : <DeleteOutlined />}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton
            buttonsData={[
              {
                label: "Add card",
                icon: <PlusOutlined />,
                onClick: openAdd,
              },
              {
                label: "Cancel",
                icon: <CloseOutlined />,
                onClick: () => setIsModalOpen(false),
              },
            ]}
          />
        </div>
        <ConfirmModal
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
          onDeleteConfirm={handleDeleteCard}
          setSelectedRowKeys={() => setCardToDelete(null)}
          content="Do you want to delete this payment method?"
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[4px] overflow-hidden">
      {shellHeader("Add card")}
      <Form
        validateTrigger="onChange"
        form={form}
        layout="vertical"
        autoComplete="off"
        scrollToFirstError
      >
        <div className="mt-4 px-2">
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <CustomSelect
                label="Set as default"
                name="IsDefault"
                options={booleanOptions}
                placeholder="Please select"
                allowClear={false}
              />
            </Col>
            <Col span={24}>
              <label className="inline-flex w-full text-sm text-black form-label pl-0.5 py-0.5 mb-1">
                Card information
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <div
                id="manage-cards-square-root"
                className="relative rounded border border-[#d9d9d9] px-2 py-2 bg-white min-h-[48px] overflow-hidden"
              >
                <PaymentForm
                  applicationId={applicationId}
                  locationId={locationId}
                  cardTokenizeResponseReceived={handleTokenReceived}
                >
                  <CreditCard
                    render={(Button) => (
                      <Button
                        aria-hidden
                        css={{
                          position: "absolute",
                          left: "-9999px",
                          top: 0,
                          width: "1px",
                          height: "1px",
                          padding: 0,
                          margin: 0,
                          overflow: "hidden",
                          clipPath: "inset(50%)",
                          border: "none",
                          opacity: 0,
                        }}
                      >
                        {""}
                      </Button>
                    )}
                  />
                </PaymentForm>
              </div>
            </Col>
            <Col span={24}>
              <div className="flex items-center gap-2 text-gray-500 text-sm pl-0.5">
                <LockOutlined />
                <span>Your card is securely processed.</span>
              </div>
            </Col>
          </Row>
        </div>
        <div className="flex justify-center items-center pt-3">
          <CommonButton
            buttonsData={[
              {
                label: "Apply",
                icon: <CheckCircleOutlined />,
                onClick: handleSaveCard,
                loading: saving,
              },
              {
                label: "Cancel",
                icon: <CloseOutlined />,
                onClick: handleBackToList,
              },
            ]}
          />
        </div>
      </Form>
    </div>
  );
};
